import React from "react";
import { FiAlertTriangle, FiMinusCircle, FiPauseCircle, FiPlusCircle, FiShuffle, FiSlash, FiTrendingDown } from "react-icons/fi";
import { MacroImportReport } from "./macroDataClient";

/**
 * Tableau des écarts du classeur en cours de validation : chaque ligne nomme ce qui change, sur
 * quel sujet, et ce que cela produira si l'opérateur confirme. Un TABLEAU et non des listes,
 * parce qu'un agent qui téléverse tous les jours a besoin de balayer des colonnes stables, pas de
 * relire des paragraphes.
 *
 * <p>Ce panneau parle du fichier qu'on s'apprête à confirmer. L'état DURABLE de la base, lui, est
 * porté par le bandeau d'anomalies en tête d'écran : il survit aux imports, celui-ci non.</p>
 */

type Ligne = {
	cle: string;
	icone: React.ReactNode;
	nature: string;
	tonalite: string;
	sujet: string;
	detail: string;
	consequence: string;
};

export const StructureWarnings: React.FC<{ report: MacroImportReport }> = ({ report }) => {
	const lignes: Ligne[] = [];

	for (const sheet of report.sheetsResolved ?? []) {
		lignes.push({
			cle: `resolved-${sheet.sheetName}`,
			icone: <FiShuffle className="h-4 w-4 shrink-0 text-amber-600" />,
			nature: "Onglet renommé",
			tonalite: "bg-amber-50",
			sujet: sheet.datasetName,
			detail: `« ${sheet.sheetName} » au lieu de « ${sheet.canonicalSheet} »`,
			consequence: "Reconnu automatiquement ; les données sont publiées normalement.",
		});
	}
	for (const sheet of report.sheetsUnknown ?? []) {
		lignes.push({
			cle: `unknown-${sheet}`,
			icone: <FiSlash className="h-4 w-4 shrink-0 text-red-600" />,
			nature: "Onglet inconnu",
			tonalite: "bg-red-50",
			sujet: "-",
			detail: `« ${sheet} »`,
			consequence: "Ignoré : aucun sujet ne le réclame, ses données ne seront pas publiées.",
		});
	}
	// Un sujet dont l'onglet manque est GELÉ, pas retiré : ses données restent en ligne. La ligne
	// est donc informative, pas alarmante, et elle dit ce qui reste publié.
	const geles = new Set((report.subjectsFrozen ?? []).map((item) => item.split(" : ")[0]));
	for (const item of report.subjectsFrozen ?? []) {
		const [sujet, ...reste] = item.split(" : ");
		lignes.push({
			cle: `frozen-${item}`,
			icone: <FiPauseCircle className="h-4 w-4 shrink-0 text-slate-600" />,
			nature: "Sujet gelé",
			tonalite: "bg-slate-50",
			sujet,
			detail: reste.join(" : ") || item,
			consequence: "Rien n'est perdu : la page reste en ligne sur ses données actuelles, sans avancer.",
		});
	}
	for (const sujet of (report.subjectsMissing ?? []).filter((item) => !geles.has(item))) {
		lignes.push({
			cle: `missing-${sujet}`,
			icone: <FiMinusCircle className="h-4 w-4 shrink-0 text-red-600" />,
			nature: "Sujet absent",
			tonalite: "bg-red-50",
			sujet,
			detail: "Aucun onglet dans ce classeur",
			consequence: "Le sujet et sa page seront retirés du portail si vous confirmez.",
		});
	}
	for (const item of report.seriesRemoved ?? []) {
		const [sujet, ...reste] = item.split(" : ");
		lignes.push({
			cle: `removed-${item}`,
			icone: <FiMinusCircle className="h-4 w-4 shrink-0 text-red-600" />,
			nature: "Indicateur retiré",
			tonalite: "bg-red-50",
			sujet,
			detail: reste.join(" : ") || item,
			consequence: "Présent dans la base publiée, absent du classeur : il disparaîtra.",
		});
	}
	for (const item of report.seriesAdded ?? []) {
		const [sujet, ...reste] = item.split(" : ");
		lignes.push({
			cle: `added-${item}`,
			icone: <FiPlusCircle className="h-4 w-4 shrink-0 text-sky-600" />,
			nature: "Nouvel indicateur",
			tonalite: "bg-sky-50",
			sujet,
			detail: reste.join(" : ") || item,
			consequence: "Publié tel quel ; vérifiez son libellé et son unité.",
		});
	}
	for (const item of report.periodRegressions ?? []) {
		const [sujet, ...reste] = item.split(" : ");
		lignes.push({
			cle: `regression-${item}`,
			icone: <FiTrendingDown className="h-4 w-4 shrink-0 text-amber-700" />,
			nature: "Données retirées",
			tonalite: "bg-amber-50",
			sujet,
			detail: reste.join(" : ") || item,
			consequence: "Retiré du portail si vous confirmez : vérifiez qu'il s'agit d'une correction voulue.",
		});
	}

	if (lignes.length === 0) return null;

	return (
		<div data-testid="structure-warnings" className="mt-5 rounded-2xl border-2 border-amber-300 bg-white p-5">
			<div className="flex items-start gap-3">
				<div className="rounded-xl bg-amber-100 p-2 text-amber-700"><FiAlertTriangle className="h-5 w-5" /></div>
				<div>
					<h3 className="text-base font-bold text-amber-900">
						Changements de structure détectés ({lignes.length})
					</h3>
					<p className="mt-0.5 text-sm text-amber-800">
						Ce classeur ne recouvre pas exactement la base publiée. Passez chaque ligne en revue avant de confirmer le remplacement.
					</p>
				</div>
			</div>
			<div className="mt-4 overflow-x-auto">
				<table className="min-w-full text-sm">
					<thead>
						<tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
							<th className="px-3 py-2 font-semibold">Nature</th>
							<th className="px-3 py-2 font-semibold">Sujet</th>
							<th className="px-3 py-2 font-semibold">Détail</th>
							<th className="px-3 py-2 font-semibold">Ce que cela produira</th>
						</tr>
					</thead>
					<tbody>
						{lignes.map(ligne => (
							<tr key={ligne.cle} className={`border-b border-slate-100 align-top ${ligne.tonalite}`}>
								<td className="px-3 py-2">
									<span className="flex items-center gap-2 font-semibold text-slate-800">
										{ligne.icone}{ligne.nature}
									</span>
								</td>
								<td className="px-3 py-2 font-medium text-slate-700">{ligne.sujet}</td>
								<td className="px-3 py-2 font-mono text-xs text-slate-700">{ligne.detail}</td>
								<td className="px-3 py-2 text-slate-600">{ligne.consequence}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default StructureWarnings;
